/**
 * Shared 30/70 evaluation layout: selectable criterion list and a detail pane.
 */
import { type ReactNode, useState } from "react";
import EvaluationListCard, {
    type EvaluationListCardProps,
} from "./EvaluationListCard";

/**
 * List-row data for the split pane, excluding selection wiring.
 */
export type EvaluationListItem = Omit<EvaluationListCardProps, "onClick" | "isSelected">;

/**
 * Props for the evaluation split pane.
 */
interface EvaluationSplitPaneProps {
    /** Title shown above the criterion list. */
    heading: string;
    /** Secondary line under the heading. */
    subheading: string;
    /** Extra class on the outer split container. */
    className: string;
    /** Extra classes for the right pane; defaults to the evaluation-detail spacing. */
    rightPaneClassName?: string;
    /** Criterion rows shown in the left list. */
    items: EvaluationListItem[];
    /** Controlled selection; defaults to the first item when omitted. */
    selectedId?: string | null;
    /** Called when the user selects a list row; omit for internal selection state. */
    onSelectedIdChange?: (id: string) => void;
    /** Detail content rendered in the right pane. */
    children: ReactNode;
}

/**
 * Selectable evaluation list on the left and a detail pane on the right.
 *
 * @param props - List copy, items, and detail pane
 * @param props.heading - Title shown above the criterion list
 * @param props.subheading - Secondary line under the heading
 * @param props.className - Extra class on the outer split container
 * @param props.rightPaneClassName - Right-pane overflow, background, and spacing classes
 * @param props.items - Criterion rows shown in the left list
 * @param props.selectedId - Controlled selection when paired with onSelectedIdChange
 * @param props.onSelectedIdChange - Selection handler for controlled mode
 * @param props.children - Detail content rendered in the right pane
 * @returns Split evaluation layout
 */
const EvaluationSplitPane = ({
    heading,
    subheading,
    className,
    rightPaneClassName = "overflow-y-auto bg-white p-5",
    items,
    selectedId,
    onSelectedIdChange,
    children,
}: EvaluationSplitPaneProps) => {
    const [internalSelectedId, setInternalSelectedId] = useState<string | null>(
        items[0]?.id ?? null,
    );
    const selectedCardId =
        selectedId !== undefined ? selectedId : internalSelectedId;

    /**
     * Stores the id of the criterion row the user selected.
     *
     * @param id - Id of the clicked evaluation card
     */
    const handleEvaluationCardClick = (id: string) => {
        if (onSelectedIdChange) {
            onSelectedIdChange(id);
            return;
        }
        setInternalSelectedId(id);
    };

    return (
        <div
            className={`w-full h-full overflow-hidden border border-primaryGray-14 rounded-lg flex ${className}`}
        >
            <div className="evaluation-split-pane--left-panel flex min-h-0 w-[30%] flex-col overflow-hidden">
                <div className="evaluation-split-pane--left-panel--header px-4 py-3 bg-white border-b border-primaryGray-14">
                    <div className="heading text-smaller font-bold text-primaryGray-1">
                        {heading}
                    </div>
                    <div className="sub-heading text-x-tiny font-normal text-primaryGray-8">
                        {subheading}
                    </div>
                </div>
                <div className="evaluation-split-pane--left-panel--content flex-1 overflow-y-auto min-h-0 bg-white">
                    {items.map((criterion) => (
                        <EvaluationListCard
                            key={criterion.id}
                            {...criterion}
                            isSelected={criterion.id === selectedCardId}
                            onClick={handleEvaluationCardClick}
                        />
                    ))}
                </div>
            </div>
            <div
                className={`evaluation-split-pane--right-panel min-h-0 w-[70%] border-l border-primaryGray-14 ${rightPaneClassName}`}
            >
                {children}
            </div>
        </div>
    );
};

export default EvaluationSplitPane;
